<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	defaultHeight: {
		type: Number,
		default: 0,
	},
	buttonMoreText: {
		type: String,
		default: '',
	},
	buttonLessText: {
		type: String,
		default: '',
	},
	isShowIcon: {
		type: Boolean,
		default: true,
	},
	numberOfClampLines: {
		type: Number,
		default: 0,
	},
	clampLineHeightUnit: {
		type: Number,
		default: 0,
	},
})

const isExpand = ref(false)
const maxHeightStyle = ref('0')
const isOverHeight = ref(false)
const refCollapse = ref<HTMLElement | null>(null)

const isDisplayByClampLines = computed(() => {
	return props.numberOfClampLines > 0 && props.clampLineHeightUnit > 0
})

const isRenderClampStyle = computed(() => {
	return !isExpand.value && isDisplayByClampLines.value
})

const isRenderClampStyleDelay = ref(true)

const displayButtonText = computed(() => {
	if (!isExpand.value) {
		return props.buttonMoreText ? props.buttonMoreText : 'See More'
	}
	return props.buttonLessText ? props.buttonLessText : 'See Less'
})

function checkMaxHeight() {
	if (!refCollapse.value) return
	if (isExpand.value) {
		maxHeightStyle.value = `${refCollapse.value.scrollHeight}px`
	} else if (isDisplayByClampLines.value) {
		maxHeightStyle.value = `${
			props.numberOfClampLines * props.clampLineHeightUnit
		}px`
	} else {
		maxHeightStyle.value = `${props.defaultHeight}px`
	}
	isOverHeight.value = isDisplayByClampLines.value
		? refCollapse.value.scrollHeight >
		  props.numberOfClampLines * props.clampLineHeightUnit
		: refCollapse.value.scrollHeight > props.defaultHeight * 1.5
	if (!isOverHeight.value) isExpand.value = true
}

function changeExpandStatus(e: MouseEvent) {
	const target = e.target as HTMLElement
	if (target && target.tagName === 'A') {
		return false
	}
	if (!isOverHeight.value) return false

	isExpand.value = !isExpand.value
	return true
}

function toggleIsRenderClampStyleDelay() {
	isRenderClampStyleDelay.value = !isRenderClampStyleDelay.value
}

onMounted(() => {
	checkMaxHeight()
})

watch(
	() => isExpand.value,
	() => {
		checkMaxHeight()
	},
)

watch(
	() => isRenderClampStyle.value,
	value => {
		if (!value) {
			toggleIsRenderClampStyleDelay()
		} else {
			setTimeout(() => {
				toggleIsRenderClampStyleDelay()
			}, 300)
		}
	},
)
</script>

<template>
	<div class="bs-collapse">
		<div
			class="bs-collapse-content"
			:class="{
				'bs-collapse-content--expand': isExpand,
				'bs-collapse-content--clamp': isRenderClampStyleDelay,
			}"
			:style="{
				'max-height': maxHeightStyle,
				'-webkit-line-clamp': isRenderClampStyleDelay
					? numberOfClampLines
					: undefined,
			}"
			ref="refCollapse"
		>
			<slot name="content" />
		</div>
		<div
			class="bs-collapse-button"
			v-if="isOverHeight"
			@click="changeExpandStatus"
		>
			<span
				:class="[
					{ 'bs-collapse-button__text': isShowIcon },
					{
						'typography-s1-reg select-none pt-8px text-o-500 underline':
							!isShowIcon,
					},
				]"
			>
				{{ displayButtonText }}
			</span>
			<BaseIcon
				v-if="isShowIcon"
				:name="'arrow_down'"
				class="bs-collapse-icon h-[14px] w-[14px] fill-o-500"
				:class="{
					'bs-collapse-icon__arrow-down': !isExpand,
					'bs-collapse-icon__arrow-up': isExpand,
				}"
			></BaseIcon>
		</div>
	</div>
</template>
